/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useRef } from 'react';
import { AuthWrapper, PrivateRoute } from 'modules/common/auth/components';
import {
  ADMIN_ROLE,
  DEMO_ROLE,
  SUPER_ADMIN_ROLE,
  USER_ROLE,
  PREFERRED_ROLE,
  PREFERRED_ADMIN_ROLE,
} from 'modules/common/constants/roles';
import ROUTES from 'modules/common/constants/route';
import AuthLayout from 'modules/common/layouts/auth';
import DashboardLayout from 'modules/common/layouts/dashboard';
import { EventCategoriesConfigureView } from 'modules/event-configuration/components';
import { DashboardView } from 'modules/dashboard/components';
import Page404 from 'modules/error-pages/404';
import { ForgotPasswordView } from 'modules/forgot-password/components';
import { RateCalendarUploadView } from 'modules/rate-calendar-upload/components';
import { RateCalendarView } from 'modules/rate-calendar/components';
import { ResetPasswordView } from 'modules/reset-password/components';
import { UsersView } from 'modules/users/components';
import VerifyCallback from 'modules/verify-callback/components';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HotelRoomsConfigureView } from 'modules/hotel-configuration/components';
import { SignUpView } from 'modules/sign-up/components';
import { EXECUTION_TYPE, UiController, decodedTabs } from 'modules/common/ui-controller';
import { ENVIRONMENT } from 'config';
import {
  decodeLegacyCommandUrl,
  isTabAllowedForRole,
  setHotelCommand,
} from 'modules/common/ui-controller/utils/functions';
import { runCommandSet } from 'modules/common/ui-controller/hooks/use-apply-command-url';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  selectHotelChangeState,
  selectShareUrlTabList,
  selectTabChangeState,
  selectUserRole,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import useIsAuthenticated from 'modules/common/auth/hooks/use-is-authenticated';
import SignInAuthWindow from 'modules/sign-in/components/auth-window';
import SignInLayout from 'modules/common/layouts/sign-in';
import { SignInView } from 'modules/sign-in/components';
import UserManual from 'modules/user-manual/components';
import FAQView from 'modules/faq/components';
import SupportView from 'modules/support/components';
import { isEmpty } from '../modules/common/helpers/object';

/**
 * Define the routeing structure using array. Here include the nested routeing as well.
 * And also define the auth wrapper and private routers for helping to role based routes
 */
const setRoutes = () => {
  // get current path location
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const { isAuthenticated } = useIsAuthenticated();
  // reference to execute ui controller command with search value
  const searchCommandsRef = useRef(null);
  // location that is already applied, to apply the url only once per navigation
  const appliedLocationRef = useRef(null);
  // whether url commands are waiting for tab and hotel changes
  const pendingCommandsRef = useRef(false);
  // global selectors fot tab and hotel name change
  const tabChange = useSelector(selectTabChangeState);
  const hotelChange = useSelector(selectHotelChangeState);
  const userRole = useSelector(selectUserRole);
  const visualFilters = useSelector(selectVisualFilters);
  const TABS = useSelector(selectShareUrlTabList);
  // search string of the url, share urls copied while url encryption was on are decoded
  const search = decodeLegacyCommandUrl(location?.search);
  // function ref to execute ui controller commands of the url search string
  searchCommandsRef.current = () => {
    // execute url filter options
    runCommandSet(dispatch, search, EXECUTION_TYPE.URL);
  };
  // triggers when location path changes
  useEffect(() => {
    if (appliedLocationRef.current === location?.key) return;
    // tab part is completed when there is no tab to set or it has been set
    let isTabApplied = !location?.hash;
    // check location hash and set the tab
    if (!isEmpty(TABS) && location?.hash && visualFilters) {
      dispatch(dashboardActions.setTabChange(false));
      const tab = decodedTabs[location?.hash.substring(1)];
      if (tab === undefined) {
        isTabApplied = true;
      } else if (isAuthenticated()) {
        if (!isTabAllowedForRole(tab, userRole)) {
          navigate(ROUTES.DASHBOARD);
          return;
        }
        UiController.setTab(tab);
        dispatch(dashboardActions.setUrlTab(TABS[tab]));
        isTabApplied = true;
      }
    }
    // retrieve hotel command and set hotel name
    if (location?.search && visualFilters) {
      dispatch(dashboardActions.setHotelChange(false));
      setHotelCommand(search, EXECUTION_TYPE.URL);
      pendingCommandsRef.current = true;
    }
    if (visualFilters && isTabApplied) {
      appliedLocationRef.current = location?.key;
    }
  }, [location, isAuthenticated, visualFilters, TABS]);
  // triggers when tab and hotel commands are executed
  useEffect(() => {
    // call for command execution function after tab and hotel commands are executed
    // tab and hotel change states are read from the store: the values of this render can be the ones
    // restored from a previous url, before the tab and hotel commands of this url reset them
    const state = store.getState();
    if (
      selectTabChangeState(state) &&
      selectHotelChangeState(state) &&
      location?.search &&
      pendingCommandsRef.current
    ) {
      pendingCommandsRef.current = false;
      searchCommandsRef.current();
    }
  }, [tabChange, hotelChange]);
  // define route configurations
  const routes = [
    {
      path: ROUTES.ROOT,
      element: <Navigate to={ROUTES.LOGIN} />,
    },
    {
      path: ROUTES.ROOT,
      element: <SignInLayout />,
      children: [
        {
          path: ROUTES.LOGIN,
          element: <SignInView />,
        },
        {
          path: ROUTES.LOGIN_REDIRECT,
          element: <SignInAuthWindow />,
        },
      ],
    },
    {
      path: ROUTES.ROOT,
      element: <AuthLayout />,
      children: [
        {
          path: ROUTES.FORGOT_PASSWORD,
          element: <ForgotPasswordView />,
        },
        {
          path: ROUTES.RESET_PASSWORD,
          element: <ResetPasswordView />,
        },
        {
          path: ROUTES.SIGN_UP,
          element: <SignUpView />,
        },
      ],
    },
    {
      path: ROUTES.DASHBOARD,
      element: (
        <AuthWrapper>
          <DashboardLayout />
        </AuthWrapper>
      ),
      children: [
        {
          path: '',
          hash: location.hash,
          search: location.search ?? '',
          element: (
            <PrivateRoute
              component={DashboardView}
              roles={[
                SUPER_ADMIN_ROLE,
                ADMIN_ROLE,
                USER_ROLE,
                DEMO_ROLE,
                PREFERRED_ROLE,
                PREFERRED_ADMIN_ROLE,
              ]}
            />
          ),
        },
        ENVIRONMENT.ENABLE_BULK_UPLOAD.toLowerCase() === 'true' && {
          path: ROUTES.FORECAST_UPLOAD,
          element: (
            <PrivateRoute
              component={RateCalendarUploadView}
              roles={[
                SUPER_ADMIN_ROLE,
                ADMIN_ROLE,
                USER_ROLE,
                DEMO_ROLE,
                PREFERRED_ROLE,
                PREFERRED_ADMIN_ROLE,
              ]}
            />
          ),
        },
      ],
    },
    {
      path: ROUTES.RATE_CALENDAR,
      element: (
        <AuthWrapper>
          <DashboardLayout />
        </AuthWrapper>
      ),
      children: [
        {
          path: '',
          element: (
            <PrivateRoute
              component={RateCalendarView}
              roles={[
                SUPER_ADMIN_ROLE,
                ADMIN_ROLE,
                USER_ROLE,
                DEMO_ROLE,
                PREFERRED_ROLE,
                PREFERRED_ADMIN_ROLE,
              ]}
            />
          ),
        },
      ],
    },
    {
      path: ROUTES.CONFIGURE,
      element: (
        <AuthWrapper>
          <DashboardLayout />
        </AuthWrapper>
      ),
      children: [
        {
          path: ROUTES.CONFIGURE_HOTELS,
          element: (
            <PrivateRoute
              component={HotelRoomsConfigureView}
              roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE]}
            />
          ),
        },
        {
          path: ROUTES.EVENT_CATEGORIES,
          element: (
            <PrivateRoute
              component={EventCategoriesConfigureView}
              roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE]}
            />
          ),
        },
        {
          path: ROUTES.USERS,
          element: (
            <PrivateRoute
              component={UsersView}
              roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
            />
          ),
        },
      ],
    },
    {
      path: ROUTES.NOT_FOUND,
      element: <Page404 />,
    },
    {
      path: ROUTES.VERIFY_CALLBACK,
      element: <VerifyCallback />,
    },
    {
      path: ROUTES.USER_MANUAL,
      element: <UserManual />,
    },
    {
      path: ROUTES.FAQ,
      element: <FAQView />,
    },
    {
      path: ROUTES.SUPPORT,
      element: <SupportView />,
    },
  ];
  return routes;
};
//
export default setRoutes;
